import { useRef, useState, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

const Entry = () => {
  const sectionRef = useRef(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  useGSAP(
    () => {
      const pref = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (pref) {
        gsap.set([".e-name", ".e-img", ".e-topbar", ".e-bg"], { opacity: 1, y: 0 });
        return;
      }

      gsap.set(".e-topbar", { opacity: 0, y: -12 });
      gsap.set(".e-name", { opacity: 0, y: 28 });
      gsap.set(".e-img", { y: "100vh" });
      gsap.set(".e-bg", { opacity: 0 });

      const tl = gsap.timeline({ delay: 0.1 });

      tl.to(".e-bg", { opacity: 1, duration: 1.2, ease: "power2.out" })
        .to(".e-topbar", { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" }, "-=0.8")
        .to(".e-name", { opacity: 1, y: 0, duration: 1.0, ease: "power3.out" }, "-=0.4")
        .to(".e-img", { y: 0, duration: 1.45, ease: "expo.out" }, "-=0.5");
    },
    { scope: sectionRef },
  );

  return (
    <section
      ref={sectionRef}
      id="entry"
      className="relative h-dvh w-screen overflow-hidden bg-white"
    >
      {/* ── BACKGROUND — curly lines texture, multiply blend makes white invisible ── */}
      <div className="e-bg pointer-events-none absolute inset-0 z-0">
        <img
          src="/img/curly_lines.webp"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="h-full w-full select-none object-cover"
          style={{ mixBlendMode: "multiply", opacity: 0.38 }}
        />
      </div>

      {/* ── TOP BAR — PORTFOLIO:VR02 + audio ── */}
      <div className="e-topbar absolute top-0 left-0 right-0 z-20 flex h-18 items-center justify-between px-3 sm:px-8 lg:px-14">
        <span
          className="font-robert-medium uppercase"
          style={{
            fontSize: "clamp(9px, 1.1vw, 15px)",
            letterSpacing: "0.22em",
            fontWeight: 700,
            color: "#111111",
          }}
        >
          Portfolio:VR02
        </span>

        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Hire Me */}
          <a
            href="mailto:vishvjeet.rana7@gmail.com"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "clamp(32px,3.2vw,48px)",
              paddingInline: "clamp(10px,1.4vw,22px)",
              borderRadius: "9999px",
              fontSize: "clamp(9px,0.9vw,12px)",
              fontFamily: "var(--font-robert-medium)",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textDecoration: "none",
              textTransform: "uppercase",
              background: "#111111",
              color: "#ffffff",
              transition: "background 0.25s, color 0.25s",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#ffffff";
              e.currentTarget.style.color = "#111111";
              e.currentTarget.style.boxShadow = "inset 0 0 0 1.5px #111111";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#111111";
              e.currentTarget.style.color = "#ffffff";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Hire Me
          </a>

          {/* X handle */}
          <a
            href="https://x.com/RVishvjeet_"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "clamp(32px,3.2vw,48px)",
              paddingInline: "clamp(10px,1.2vw,18px)",
              borderRadius: "9999px",
              border: "1.5px solid rgba(13,13,13,0.3)",
              fontSize: "clamp(9px,0.9vw,12px)",
              fontFamily: "var(--font-robert-medium)",
              fontWeight: 700,
              letterSpacing: "0.10em",
              color: "#111111",
              textDecoration: "none",
              textTransform: "uppercase",
              background: "transparent",
              transition: "background 0.25s, color 0.25s, border-color 0.25s",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#111111";
              e.currentTarget.style.color = "#ffffff";
              e.currentTarget.style.borderColor = "#111111";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#111111";
              e.currentTarget.style.borderColor = "rgba(13,13,13,0.3)";
            }}
          >
            @RVishvjeet_
          </a>

          {/* Audio button */}
          <button
            onClick={() => setIsPlaying((p) => !p)}
            className="flex items-center justify-center gap-[5px] rounded-full border border-[#0D0D0D]/30 transition-colors duration-300 hover:border-[#0D0D0D]/60 cursor-pointer"
            style={{ height: "clamp(32px,3.2vw,48px)", width: "clamp(32px,3.2vw,48px)" }}
            aria-label={isPlaying ? "Pause ambient audio" : "Play ambient audio"}
          >
            <audio ref={audioRef} src="/audio/sunset.mp3" loop className="hidden" />
            {[1, 2, 3, 4].map((bar) => (
              <div
                key={bar}
                className={`indicator-line ${isPlaying ? "active" : ""}`}
                style={{ animationDelay: `${bar * 0.1}s` }}
                aria-hidden="true"
              />
            ))}
          </button>
        </div>
      </div>

      {/* ── NAME — carbon-black shine, z-10 sits above bg texture ── */}
      <h1
        className="e-name carbon-shine absolute bottom-[5%] left-0 right-0 z-10 text-center font-robert-medium font-extrabold"
        style={{
          fontSize: "clamp(4rem, 13vw, 13rem)",
          lineHeight: 0.92,
          letterSpacing: "-0.03em",
        }}
      >
        Vishvjeet Rana
      </h1>

      {/* ── CHARACTER IMAGE — z-20, slides up from below ── */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex justify-center">
        <div className="e-img">
          <img
            src="/img/wave_hand.png"
            alt="Vishvjeet Rana"
            draggable={false}
            className="block select-none"
            style={{
              height: "clamp(360px, 88vh, 1020px)",
              width: "auto",
              objectFit: "contain",
              mixBlendMode: "multiply",
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default Entry;
